import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["banner", "installModal"]
  static values = {
    appName: String,
    appColor: String,
    themeColor: String,
    logoUrl: String
  }

  connect() {
    this.deferredPrompt = null
    this.mobileBannerShown = false

    window.addEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt.bind(this))

    // Add debug helper to window for testing
    window.resetPWABanner = () => {
      localStorage.removeItem('pwa-banner-dismissed')
      localStorage.removeItem('pwa-installed')
      location.reload()
    }

    // Check if banner was dismissed
    if (!this.isBannerDismissed()) {
      this.checkAndShowBanner()
    }
  }

  disconnect() {
    window.removeEventListener('beforeinstallprompt', this.handleBeforeInstallPrompt.bind(this))
  }

  handleBeforeInstallPrompt(event) {
    event.preventDefault()
    this.deferredPrompt = event
    this.showInstallPrompts()
  }

  checkAndShowBanner() {
    if (!this.isInStandaloneMode()) {
      setTimeout(() => {
        if (this.isPWAInstalled()) {
          this.showMobileBanner('open')
        } else {
          // Show install banner on both mobile and desktop
          this.showMobileBanner('install')
        }
      }, 300)
    }
  }

  showInstallPrompts() {
    if (this.isMobileDevice() && !this.isInStandaloneMode() && !this.mobileBannerShown) {
      if (this.isPWAInstalled()) {
        this.showMobileBanner('open')
      } else {
        this.showMobileBanner('install')
      }
    }
  }

  showMobileBanner(type = 'install') {
    if (this.hasBannerTarget) {
      this.updateBannerContent(type)
      this.bannerTarget.style.display = 'block'
      document.body.style.paddingTop = this.bannerTarget.offsetHeight + 'px'
      this.mobileBannerShown = true
    }
  }

  updateBannerContent(type) {
    const titleElement = this.bannerTarget.querySelector('[data-pwa-title]')
    const subtitleElement = this.bannerTarget.querySelector('[data-pwa-subtitle]')
    const actionButton = this.bannerTarget.querySelector('[data-pwa-action]')

    if (type === 'install') {
      if (titleElement) titleElement.textContent = `Install ${this.appNameValue}`
      if (subtitleElement) subtitleElement.textContent = 'Get the full app experience'
      if (actionButton) {
        // Show Install button on both mobile and desktop
        actionButton.textContent = 'Install'
        actionButton.setAttribute('data-action', 'click->pwa#showInstallInstructions')
        actionButton.style.display = 'block'
      }
    } else if (type === 'open') {
      if (titleElement) titleElement.textContent = `Open ${this.appNameValue}`
      if (subtitleElement) subtitleElement.textContent = 'Continue in the app'
      if (actionButton) {
        actionButton.textContent = 'Open App'
        actionButton.setAttribute('data-action', 'click->pwa#openApp')
        actionButton.style.display = 'block'
      }
    }
  }

  // Show installation instructions modal (mobile and desktop)
  showInstallInstructions() {
    if (this.hasInstallModalTarget) {
      this.installModalTarget.style.display = 'flex'
      document.body.style.overflow = 'hidden'

      // Show relevant instruction sections based on device
      this.showRelevantInstructions()
    }
  }

  // Show only relevant instructions based on user's device
  showRelevantInstructions() {
    const androidInstructions = this.installModalTarget.querySelector('[data-pwa-target="androidInstructions"]')
    const iosInstructions = this.installModalTarget.querySelector('[data-pwa-target="iosInstructions"]')
    const webInstructions = this.installModalTarget.querySelector('[data-pwa-target="webInstructions"]')

    if (this.isMobileDevice()) {
      // Mobile device - show Android/iOS, hide Web
      if (/Android/i.test(navigator.userAgent)) {
        if (androidInstructions) androidInstructions.style.display = 'block'
        if (iosInstructions) iosInstructions.style.display = 'none'
      } else if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
        if (androidInstructions) androidInstructions.style.display = 'none'
        if (iosInstructions) iosInstructions.style.display = 'block'
      } else {
        // Other mobile - show both
        if (androidInstructions) androidInstructions.style.display = 'block'
        if (iosInstructions) iosInstructions.style.display = 'block'
      }
      if (webInstructions) webInstructions.style.display = 'none'
    } else {
      // Desktop - show only Web instructions
      if (androidInstructions) androidInstructions.style.display = 'none'
      if (iosInstructions) iosInstructions.style.display = 'none'
      if (webInstructions) webInstructions.style.display = 'block'
    }
  }

  // Close installation instructions modal
  closeInstallModal() {
    if (this.hasInstallModalTarget) {
      this.installModalTarget.style.display = 'none'
      document.body.style.overflow = ''
    }
  }

  // Prevent modal from closing when clicking on content
  stopPropagation(event) {
    event.stopPropagation()
  }

  // Optional native install prompt (when available)
  install() {
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt()
      this.deferredPrompt.userChoice.then((choiceResult) => {
        if (choiceResult.outcome === 'accepted') {
          localStorage.setItem('pwa-installed', 'true')
          this.closeBanner()
        }
        this.deferredPrompt = null
      })
    }
  }

  openApp() {
    const appUrl = window.location.origin + window.location.pathname

    if (/Android/i.test(navigator.userAgent) && !this.isLocalhost()) {
      try {
        const intentUrl = `intent://${window.location.host}${window.location.pathname}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${encodeURIComponent(appUrl)};end`
        window.location.href = intentUrl
        return
      } catch (error) {
        // fall back
      }
    }

    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      this.closeBanner()
      alert('To open the app, please switch to the installed app from your home screen.')
      return
    }

    if (this.isLocalhost()) {
      this.closeBanner()
      return
    }

    window.location.href = appUrl
  }

  closeBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.style.display = 'none'
      document.body.style.paddingTop = '0px'

      // Store dismissal in localStorage
      this.setBannerDismissed()
    }
  }

  // Helpers
  isMobileDevice() {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || (window.innerWidth <= 768)
  }

  isPWAInstalled() {
    return localStorage.getItem('pwa-installed') === 'true'
  }

  isInStandaloneMode() {
    return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true
  }

  isLocalhost() {
    return ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname)
  }

  // LocalStorage helpers for banner dismissal
  isBannerDismissed() {
    return localStorage.getItem('pwa-banner-dismissed') === 'true'
  }

  setBannerDismissed() {
    localStorage.setItem('pwa-banner-dismissed', 'true')
  }
}
